import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Web MIDI INPUT — the listening half of the lesson piano.
 *
 * `audio/piano.ts` is OUTPUT (we make sound). This is INPUT (the student's
 * physical/virtual MIDI keyboard tells us what they played) so a lesson can
 * grade their playing against a target.
 *
 *  • Feature-detects `navigator.requestMIDIAccess` — older Safari / Firefox
 *    without the flag simply report `status: "unsupported"` and the caller
 *    falls back to the on-screen keyboard.
 *  • Permission is requested lazily from a user gesture (enable()), never on
 *    mount, so we don't trip the browser's permission prompt unprompted.
 *  • Hot-plug aware: attaching/detaching a controller updates the device list
 *    and re-binds listeners via `onstatechange`.
 *  • Parses raw status bytes: 0x90 note-on (velocity>0) and 0x80 / note-on
 *    velocity-0 note-off. Channel is ignored (we accept all 16).
 */

const PITCH_LETTERS = ["C", "C#", "D", "D#", "E", "F", "F#", "G", "G#", "A", "A#", "B"];

export function midiToPitchName(midi: number): string {
  const pc = ((midi % 12) + 12) % 12;
  const oct = Math.floor(midi / 12) - 1;
  return `${PITCH_LETTERS[pc]}${oct}`;
}

export interface MidiInputNote {
  /** Raw MIDI note number 21..108. */
  midi: number;
  /** Pitch class 0..11 (octave-agnostic). */
  pc: number;
  /** Scientific pitch name, e.g. "C4", "F#5". */
  pitchName: string;
  /** Normalised velocity 0..1. */
  velocity: number;
  /** DOMHighResTimeStamp of the event. */
  time: number;
}

export type MidiStatus =
  | "unsupported" // browser has no Web MIDI
  | "idle" // supported, not yet requested
  | "requesting"
  | "ready" // access granted, listening
  | "denied" // the user (or a remembered choice) blocked it
  | "blocked-by-frame"; // an embedding page did not allow="midi"

export interface UseMidiInput {
  status: MidiStatus;
  /** Connected input device names (for the UI to show "Roland FP-30" etc.). */
  devices: string[];
  /** Lazily request access + start listening. Call from a click handler. */
  enable: () => Promise<void>;
  /** True when this page is inside an iframe — recovery differs there. */
  embedded: boolean;
  /** Stop listening and drop the access handle. */
  disable: () => void;
  error: string | null;
}

interface Handlers {
  onNoteOn?: (n: MidiInputNote) => void;
  onNoteOff?: (n: MidiInputNote) => void;
}

function isSupported(): boolean {
  return typeof navigator !== "undefined" && typeof navigator.requestMIDIAccess === "function";
}

export function useMidiInput(handlers: Handlers): UseMidiInput {
  const [status, setStatus] = useState<MidiStatus>(() => (isSupported() ? "idle" : "unsupported"));
  const [devices, setDevices] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);

  // Keep the latest callbacks in a ref so re-renders don't re-bind MIDI listeners.
  const handlersRef = useRef<Handlers>(handlers);
  handlersRef.current = handlers;

  const accessRef = useRef<MIDIAccess | null>(null);
  // Generation counter: bumps on every enable()/disable() so an in-flight
  // requestMIDIAccess that resolves after a disable (or a second enable click)
  // is discarded instead of binding a duplicate, un-unbindable listener set —
  // which would make every physical keypress grade twice.
  const genRef = useRef(0);
  const requestingRef = useRef(false);

  const handleMessage = useCallback((e: MIDIMessageEvent) => {
    const data = e.data;
    if (!data || data.length < 2) return;
    const statusByte = data[0] & 0xf0;
    const note = data[1];
    const velocityRaw = data.length > 2 ? data[2] : 0;
    const velocity = velocityRaw / 127;
    const payload: MidiInputNote = {
      midi: note,
      pc: ((note % 12) + 12) % 12,
      pitchName: midiToPitchName(note),
      velocity,
      time: e.timeStamp,
    };
    // Note-on with zero velocity is the running-status convention for note-off.
    if (statusByte === 0x90 && velocityRaw > 0) {
      handlersRef.current.onNoteOn?.(payload);
    } else if (statusByte === 0x80 || (statusByte === 0x90 && velocityRaw === 0)) {
      handlersRef.current.onNoteOff?.(payload);
    }
  }, []);

  const bindInputs = useCallback(
    (access: MIDIAccess) => {
      const names: string[] = [];
      access.inputs.forEach((input) => {
        names.push(input.name ?? "MIDI device");
        input.onmidimessage = handleMessage;
      });
      setDevices(names);
    },
    [handleMessage]
  );

  const enable = useCallback(async () => {
    if (!isSupported()) {
      setStatus("unsupported");
      return;
    }
    if (requestingRef.current) return; // a request is already in flight
    if (accessRef.current) {
      // already enabled — just refresh the device list
      bindInputs(accessRef.current);
      setStatus("ready");
      return;
    }
    requestingRef.current = true;
    const gen = ++genRef.current;
    setStatus("requesting");
    setError(null);
    try {
      const access = await navigator.requestMIDIAccess({ sysex: false });
      if (gen !== genRef.current) {
        // disable() (or unmount) happened while we were waiting — discard.
        access.inputs.forEach((input) => { input.onmidimessage = null; });
        return;
      }
      accessRef.current = access;
      bindInputs(access);
      access.onstatechange = () => bindInputs(access);
      setStatus("ready");
    } catch (err) {
      if (gen === genRef.current) {
        setError(err instanceof Error ? err.message : "MIDI access was blocked");
        setStatus("denied");
      }
    } finally {
      requestingRef.current = false;
    }
  }, [bindInputs]);

  const disable = useCallback(() => {
    genRef.current += 1; // invalidate any in-flight enable()
    const access = accessRef.current;
    if (access) {
      access.inputs.forEach((input) => {
        input.onmidimessage = null;
      });
      access.onstatechange = null;
    }
    accessRef.current = null;
    setDevices([]);
    setStatus(isSupported() ? "idle" : "unsupported");
  }, []);

  // Detach listeners on unmount so a backgrounded lesson stops receiving events.
  useEffect(() => {
    return () => {
      genRef.current += 1; // invalidate any in-flight enable()
      const access = accessRef.current;
      if (access) {
        access.inputs.forEach((input) => {
          input.onmidimessage = null;
        });
        access.onstatechange = null;
      }
      accessRef.current = null;
    };
  }, []);

  const embedded = typeof window !== "undefined" && window.self !== window.top;
  return { status, devices, enable, disable, error, embedded };
}
